"use client";

import {
  ConsentProvider,
  CookieBanner,
  type CookiePreferencesCopy,
  CookiePreferencesDialog,
} from "@ethera/ui/consent";
import type { ConsentReceipt } from "@guru/shared/consent";
import { applyConsent } from "@/lib/posthog-consent";

const POLICY_VERSION = "2026-05-12";

export const PREFERENCES_COPY: CookiePreferencesCopy = {
  title: "Cookie preferences",
  description:
    "Choose which cookies we may use. Essential cookies keep the site working and cannot be switched off.",
  acceptAll: "Accept all",
  rejectAll: "Only essential",
  savePreferences: "Save preferences",
  alwaysOn: "Always on",
  categories: [
    {
      id: "essential",
      title: "Essential",
      description: "Required for the site to function and to remember your settings.",
    },
    {
      id: "analytics",
      title: "Analytics",
      description: "Helps us understand how the site is used so we can improve it.",
    },
    {
      id: "sessionReplay",
      title: "Session replays",
      description: "Records sessions to diagnose problems faster. Sensitive fields are masked.",
    },
  ],
};

function persist(receipt: ConsentReceipt): void {
  void fetch("/api/consent", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(receipt),
    keepalive: true,
  }).catch(() => {});
}

/** Wraps the marketing site; the banner + preference centre share this context. */
export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
  return (
    <ConsentProvider
      policyVersion={POLICY_VERSION}
      withdrawalLink="/cookies"
      onChange={applyConsent}
      onPersistReceipt={persist}
    >
      {children}
      <CookieBanner
        copy={{
          body: "We use essential cookies to keep the site working. With your permission we also use analytics to understand how the product is used, and session replays to diagnose problems faster. You can change these at any time from the Cookie preferences link in the footer.",
          acceptAll: "Accept all",
          essentialOnly: "Only essential",
          customise: "Customise",
        }}
      />
      <CookiePreferencesDialog copy={PREFERENCES_COPY} />
    </ConsentProvider>
  );
}
